import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { formatDayMonth, formatDayMonthTime } from '../utilities/schedulerApi';
import { scoreText } from '../utilities/matchesApi';
import {
  MATCH_REPORT_FLAGS,
  champDatesPayload,
  datesAreValid,
  defaultDateRange,
  extractMatchesReport,
  extractStaffReport,
  extractSummary,
  personName,
} from '../utilities/reportsApi';
import { ReportBlock, ReportDateRange, ReportPage, ReportSearch, StadiumThumb, TeamThumb } from '../components/ReportUi';
import '../styles/stadiumsManagement.css';
import '../styles/reports.css';

const initialRange = defaultDateRange();

function MatchPair({ match }) {
  const home = match?.home_team || {};
  const guest = match?.guest_team || {};
  const homeName = home.short_name || home.name || '';
  const guestName = guest.short_name || guest.name || '';
  const hasScore = match?.home_team_scores != null && match?.guest_team_scores != null;
  return (
    <div className="rp-match-link">
      <TeamThumb team={home} />
      <span className="rp-match-name">{homeName || '—'}</span>
      <span className="rp-match-score">{hasScore ? scoreText(match) : '–'}</span>
      <span className="rp-match-name is-guest">{guestName || '—'}</span>
      <TeamThumb team={guest} />
    </div>
  );
}

function matchCardPath(match) {
  const id = match?.id ?? match?.match_id;
  return id ? `/manage_match/${id}` : '';
}

function Reports() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();

  const [summaryDates, setSummaryDates] = useState(initialRange);
  const [summary, setSummary] = useState({ matches_count: 0, stadiums: [], leagues: [] });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const [matchDates, setMatchDates] = useState(initialRange);
  const [matchFlag, setMatchFlag] = useState('all');
  const [matchSearch, setMatchSearch] = useState('');
  const [matchQuery, setMatchQuery] = useState('');
  const [matches, setMatches] = useState({ matches_count: 0, leagues: [] });
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState('');

  const [staffDates, setStaffDates] = useState(initialRange);
  const [staffSearch, setStaffSearch] = useState('');
  const [staffQuery, setStaffQuery] = useState('');
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [mobileTab, setMobileTab] = useState('summary');

  const mobileTabs = [
    { id: 'summary', label: t('rpTabSummary') },
    { id: 'matches', label: t('rpTabMatches') },
    { id: 'staff', label: t('rpTabStaff') },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setMatchQuery(matchSearch.trim()), 350);
    return () => clearTimeout(timer);
  }, [matchSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setStaffQuery(staffSearch.trim()), 350);
    return () => clearTimeout(timer);
  }, [staffSearch]);

  useEffect(() => {
    if (!champId) return undefined;
    if (!datesAreValid(summaryDates.from, summaryDates.to)) {
      setSummaryError(t('rpNeedDates'));
      return undefined;
    }
    let cancelled = false;
    setSummaryLoading(true);
    setSummaryError('');
    authorizedPost(dispatch, API_URLS.getAdminReportSummary, champDatesPayload(champId, summaryDates.from, summaryDates.to))
      .then((data) => {
        if (!cancelled) setSummary(extractSummary(data));
      })
      .catch((error) => {
        if (!cancelled) setSummaryError(getApiErrorMessage(error, t('rpNoData')));
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => { cancelled = true; };
  }, [champId, dispatch, summaryDates.from, summaryDates.to, t]);

  useEffect(() => {
    if (!champId) return undefined;
    if (!datesAreValid(matchDates.from, matchDates.to)) {
      setMatchesError(t('rpNeedDates'));
      return undefined;
    }
    let cancelled = false;
    setMatchesLoading(true);
    setMatchesError('');
    authorizedPost(dispatch, API_URLS.getAdminReportMatches, {
      ...champDatesPayload(champId, matchDates.from, matchDates.to),
      flag: matchFlag,
      search: matchQuery,
    })
      .then((data) => {
        if (!cancelled) setMatches(extractMatchesReport(data));
      })
      .catch((error) => {
        if (!cancelled) setMatchesError(getApiErrorMessage(error, t('rpNoData')));
      })
      .finally(() => {
        if (!cancelled) setMatchesLoading(false);
      });
    return () => { cancelled = true; };
  }, [champId, dispatch, matchDates.from, matchDates.to, matchFlag, matchQuery, t]);

  useEffect(() => {
    if (!champId) return undefined;
    if (!datesAreValid(staffDates.from, staffDates.to)) {
      setStaffError(t('rpNeedDates'));
      return undefined;
    }
    let cancelled = false;
    setStaffLoading(true);
    setStaffError('');
    authorizedPost(dispatch, API_URLS.getAdminReportStaff, {
      ...champDatesPayload(champId, staffDates.from, staffDates.to),
      search: staffQuery,
    })
      .then((data) => {
        if (!cancelled) setStaff(extractStaffReport(data));
      })
      .catch((error) => {
        if (!cancelled) setStaffError(getApiErrorMessage(error, t('rpNoData')));
      })
      .finally(() => {
        if (!cancelled) setStaffLoading(false);
      });
    return () => { cancelled = true; };
  }, [champId, dispatch, staffDates.from, staffDates.to, staffQuery, t]);

  return (
    <ReportPage
      title={t('navReports')}
      message={!champId && !champsLoading ? t('rpNeedChamp') : ''}
      tabs={mobileTabs}
      activeTab={mobileTab}
      onTabChange={setMobileTab}
    >
      <ReportBlock
        tabId="summary"
        tabActive={mobileTab === 'summary'}
        title={t('rpSummary')}
        loading={summaryLoading}
        error={summaryError}
        filters={(
          <ReportDateRange
            from={summaryDates.from}
            to={summaryDates.to}
            onFrom={(from) => setSummaryDates((prev) => ({ ...prev, from }))}
            onTo={(to) => setSummaryDates((prev) => ({ ...prev, to }))}
            t={t}
            disabled={!champId}
          />
        )}
      >
        <div className="rp-stat">{t('rpMatchesCount', { count: summary.matches_count })}</div>
        <div className="rp-section">
          <h3>{t('navStadiums')}</h3>
          {summary.stadiums.length ? summary.stadiums.map((stadium, index) => (
            <div className="rp-row" key={stadium.id ?? `stadium-${index}`}>
              <StadiumThumb stadium={stadium} />
              <div className="rp-row-main">
                <span className="rp-row-title">{stadium.name || t('rpNoStadium')}</span>
              </div>
              <span className="rp-row-count">{stadium.matches_count || 0}</span>
            </div>
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
        <div className="rp-section">
          <h3>{t('rpLeagues')}</h3>
          {summary.leagues.length ? summary.leagues.map((league) => (
            <div className="rp-row" key={league.id}>
              <div className="rp-row-main">
                <span className="rp-row-title">{league.name || t('rpUnknown')}</span>
              </div>
              <span className="rp-row-count">{league.matches_count || 0}</span>
            </div>
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
      </ReportBlock>

      <ReportBlock
        tabId="matches"
        tabActive={mobileTab === 'matches'}
        title={t('rpMatches')}
        loading={matchesLoading}
        error={matchesError}
        filters={(
          <>
            <ReportDateRange
              from={matchDates.from}
              to={matchDates.to}
              onFrom={(from) => setMatchDates((prev) => ({ ...prev, from }))}
              onTo={(to) => setMatchDates((prev) => ({ ...prev, to }))}
              t={t}
              disabled={!champId}
            />
            <label className="st-field">
              <span>{t('rpFlag')}</span>
              <select className="st-select" value={matchFlag} onChange={(event) => setMatchFlag(event.target.value)} disabled={!champId}>
                {MATCH_REPORT_FLAGS.map((item) => (
                  <option key={item.value} value={item.value}>{t(item.labelKey)}</option>
                ))}
              </select>
            </label>
            <ReportSearch
              value={matchSearch}
              onChange={setMatchSearch}
              placeholder={t('rpSearchTeams')}
              disabled={!champId}
            />
          </>
        )}
      >
        <div className="rp-stat">{t('rpMatchesCount', { count: matches.matches_count })}</div>
        {matches.leagues.length ? matches.leagues.map((league) => (
          <div className="rp-group" key={league.league_id}>
            <div className="rp-group-title">
              <span>{league.league_name || t('rpUnknown')}</span>
              <span className="rp-group-count">{league.matches_count || 0}</span>
            </div>
            {(league.matches || []).map((match) => {
              const to = matchCardPath(match);
              const body = (
                <>
                  <MatchPair match={match} />
                  <span className="rp-row-meta">
                    {formatDayMonthTime(match.date, match.time) || formatDayMonth(match.date)}
                    {match.stadium?.name ? ` · ${match.stadium.name}` : ''}
                  </span>
                  <div className="rp-tags">
                    {match.has_home_cap_lineup && !match.has_home_lineup ? <span className="rp-tag is-cap">{t('rpCapLineup')} · {t('rpHome')}</span> : null}
                    {match.has_guest_cap_lineup && !match.has_guest_lineup ? <span className="rp-tag is-cap">{t('rpCapLineup')} · {t('rpGuest')}</span> : null}
                    {matchFlag === 'no_lineup' && !match.has_home_lineup ? <span className="rp-tag is-warn">{t('rpAdminLineup')} · {t('rpHome')}</span> : null}
                    {matchFlag === 'no_lineup' && !match.has_guest_lineup ? <span className="rp-tag is-warn">{t('rpAdminLineup')} · {t('rpGuest')}</span> : null}
                  </div>
                </>
              );
              return to ? (
                <Link
                  key={match.id || match.match_id || to}
                  className="rp-match rp-match-card"
                  to={to}
                >
                  {body}
                </Link>
              ) : (
                <div className="rp-match" key={match.id || match.match_id || `${league.league_id}-${match.date}-${match.time}`}>
                  {body}
                </div>
              );
            })}
          </div>
        )) : <div className="rp-empty">{t('rpNoData')}</div>}
      </ReportBlock>

      <ReportBlock
        tabId="staff"
        tabActive={mobileTab === 'staff'}
        title={t('rpStaff')}
        loading={staffLoading}
        error={staffError}
        filters={(
          <>
            <ReportDateRange
              from={staffDates.from}
              to={staffDates.to}
              onFrom={(from) => setStaffDates((prev) => ({ ...prev, from }))}
              onTo={(to) => setStaffDates((prev) => ({ ...prev, to }))}
              t={t}
              disabled={!champId}
            />
            <ReportSearch
              value={staffSearch}
              onChange={setStaffSearch}
              placeholder={t('rpSearchStaff')}
              disabled={!champId}
            />
          </>
        )}
      >
        {staff.length ? staff.map((person) => (
          <div className="rp-group" key={person.id || person.user_id}>
            <div className="rp-row">
              <div className="rp-row-main">
                <span className="rp-row-title">{personName(person) || t('rpUnknown')}</span>
                <span className="rp-row-meta">{(person.positions || []).join(', ')}</span>
              </div>
              <span className="rp-row-count">{person.matches_count || 0}</span>
            </div>
            {(person.slots || []).map((slot) => (
              <div className="rp-sub" key={`${person.id}-${slot.slot}`}>
                {slot.position || slot.slot}: {slot.matches_count || 0}
              </div>
            ))}
          </div>
        )) : <div className="rp-empty">{t('rpNoData')}</div>}
      </ReportBlock>
    </ReportPage>
  );
}

export default Reports;
